import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSherlocVoice } from "../voice/VoiceContext";
import { sanitizeVoiceLogDetail } from "../voice/voiceLogPrivacy";
import {
  isEdenGreetingMessage,
  looksLikeDelegatedPlanningRequest,
} from "../voice/voiceInputRuntime";
import {
  createVoiceInputSessionController,
  INITIAL_VOICE_INPUT_SNAPSHOT,
  VOICE_INPUT_PHASES,
} from "../voice/voiceInputSession";
import EdenVoiceInputStatus from "../voice/EdenVoiceInputStatus";
import EdenVoiceInputButton from "../voice/EdenVoiceInputButton";
import useEdenContext from "../eden/context/useEdenContext";
import { resolveEdenDashboardContextFromPageAwareness } from "../eden/context/dashboardContext";
import { usePageAwarenessState } from "../contexts/PageAwarenessContext";
import { getToplinePerformance } from "../services/edenToplineService";
import { CANONICAL_CTR_LABEL } from "../utils/canonicalCtr";
import { formatFlightDateShort, formatFlightRange } from "../utils/flightDates";
import {
  getVerticalIntelligenceConfig,
  normalizeAdvertiserVertical,
  sanitizeVerticalIntelligenceCopy,
} from "../config/verticalIntelligenceConfig";
import {
  EDEN_ACTION_TYPES,
  resolveEdenActionIntent,
} from "../config/edenActionRegistry";
import {
  EDEN_ENTRY_PROMPT_EVENT,
  dispatchEdenEntryOpen,
  dispatchEdenEntryPrompt,
  EDEN_ENTRY_OPEN_EVENT,
  hasDismissedEdenEntryThisSession,
  setEdenEntryDismissedForSession,
} from "../config/edenEntry";
import { EdenEntryOverlay } from "./EdenEntryOverlay";
import useResolvedImageCandidates from "../hooks/useResolvedImageCandidates";
import EdenChatVisualizationRenderer from "./EdenChatVisualizationRenderer";
import EdenWorkStatusCard from "./EdenWorkStatusCard";
import EdenGovernedChangeCard from "./EdenGovernedChangeCard";
import { EdenActivityAnnouncer, EdenSpeakerAvatar, EdenThinkingLine } from "./EdenSpeakerIdentity";
import useEdenFullscreenSheet from "../hooks/useEdenFullscreenSheet";
import { looksLikeCampaignScheduleEditRequest, looksLikeCampaignScheduleQuestion } from "../eden/core/edenScheduleEditIntent";
import {
  fetchDelegatedWork,
  fetchActiveDelegatedWork,
  fetchUnreadWorkNotifications,
  markWorkNotificationRead,
  cancelDelegatedWork,
  clarifyDelegatedWork,
  retryDelegatedWork,
} from "../eden/core/edenWorkClient";
import facebookLogo from "../assets/facebook.jpg";
import instagramLogo from "../assets/instagram.jpg";
import tiktokLogo from "../assets/tiktok.jpg";
import youtubeLogo from "../assets/youtube.jpg";
import xLogo from "../assets/x.jpg";
import truthSocialLogo from "../assets/truth-social-logo.jpg";
import rumbleLogo from "../assets/rumble-logo.jpg";
import googleLogo from "../assets/google.jpg";
import redditLogo from "../assets/reddit.jpg";
import amazonLogo from "../assets/amazon.jpg";
import yahooLogo from "../assets/yahoo.png";
import metaLogo from "../assets/meta.jpg";
import audioLogo from "../assets/audio.jpg";
import ingameLogo from "../assets/ingame.jpg";
import ctvLogo from "../assets/ctv.jpg";
import videoLogo from "../assets/video.jpg";
import mobileLogo from "../assets/mobile.jpg";
import displayLogo from "../assets/display.jpg";
import doohLogo from "../assets/dooh.gif";
import emailLogo from "../assets/email.jpg";
import { sanitizeGovernedWindowLabel } from "../platformAnalytics/reportingWindowLabel";
import "./GlobalVoiceController.css";

// Phones (portrait, or landscape with little height) get the conversation as
// a full-screen sheet whenever it opens: the floating panel there is too
// small to read and leaves the composer under the keyboard. Wider screens
// open the floating panel and can expand it.
const EDEN_FULLSCREEN_MEDIA_QUERY = "(max-width: 768px), (max-height: 520px)";
function prefersFullscreenEdenChat() {
  try {
    return Boolean(window.matchMedia?.(EDEN_FULLSCREEN_MEDIA_QUERY)?.matches);
  } catch {
    return false;
  }
}

const EDEN_SHEET_FOCUSABLE =
  'button:not([disabled]), [href], textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const normalizeApiBase = (base) => String(base || "").trim().replace(/\/+$/, "");

const API_BASE_URL = normalizeApiBase(process.env.REACT_APP_BACKEND_URL || "");

const HIDDEN_ROUTES = [
  "/",
  "/login",
  "/forgot-password",
  "/reset-password",
  "/set-password",
  "/superadmin-login",
  "/authorize/return",
];

const EDEN_MEET_URL = "https://calendly.com/cphereai/consultation?month=2026-03";
const CANONICAL_REPORTING_CONTRACT = "canonical_reporting_v1";

const CHANNEL_LABELS = {
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  x: "X",
  truth_social: "Truth Social",
  rumble: "Rumble",
  google_search: "Google Search",
  audio: "Audio",
  ingame: "In-Game",
  ctv: "CTV",
  video: "Video",
  mobile: "Mobile",
  display: "Display",
  dooh: "DOOH",
  cinema: "Cinema",
  social_rollup: "Social",
  programmatic_rollup: "Programmatic",
};

const CHANNEL_HERO_ASSETS = {
  facebook: facebookLogo,
  instagram: instagramLogo,
  tiktok: tiktokLogo,
  youtube: youtubeLogo,
  x: xLogo,
  truth_social: truthSocialLogo,
  rumble: rumbleLogo,
  google_search: googleLogo,
  reddit: redditLogo,
  amazon: amazonLogo,
  yahoo: yahooLogo,
  yahoo_dsp: yahooLogo,
  meta: metaLogo,
  audio: audioLogo,
  ingame: ingameLogo,
  ctv: ctvLogo,
  video: videoLogo,
  mobile: mobileLogo,
  display: displayLogo,
  dooh: doohLogo,
  email: emailLogo,
};

const resolveTopPlatformHero = (value) => {
  const raw = String(value || "").trim();
  const key = raw.toLowerCase();
  if (!raw) return null;

  if (key === "facebook") return { src: facebookLogo, alt: "Facebook" };
  if (key === "instagram") return { src: instagramLogo, alt: "Instagram" };
  if (key === "tiktok") return { src: tiktokLogo, alt: "TikTok" };
  if (key === "youtube") return { src: youtubeLogo, alt: "YouTube" };
  if (key === "x" || key === "twitter") return { src: xLogo, alt: "X" };
  if (key === "truth social" || key === "truthsocial") {
    return { src: truthSocialLogo, alt: "Truth Social" };
  }
  if (key === "rumble") return { src: rumbleLogo, alt: "Rumble" };
  if (key === "google search" || key === "google" || key === "search") {
    return { src: googleLogo, alt: "Google Search" };
  }
  if (key === "reddit") return { src: redditLogo, alt: "Reddit" };
  if (key === "amazon") return { src: amazonLogo, alt: "Amazon" };
  if (key === "yahoo" || key === "yahoo_dsp" || key === "yahoo dsp") return { src: yahooLogo, alt: "Yahoo DSP" };
  if (key === "meta" || key === "social") return { src: metaLogo, alt: raw };
  if (key === "audio") return { src: audioLogo, alt: "Audio" };
  if (key === "in-game" || key === "ingame") return { src: ingameLogo, alt: "In-Game" };
  if (key === "ctv") return { src: ctvLogo, alt: "CTV" };
  if (key === "video" || key === "online video (olv)" || key === "olv") {
    return { src: videoLogo, alt: raw };
  }
  if (key === "mobile") return { src: mobileLogo, alt: "Mobile" };
  if (key === "display") return { src: displayLogo, alt: "Display" };
  if (key === "dooh") return { src: doohLogo, alt: "DOOH" };
  if (key === "email") return { src: emailLogo, alt: "Email" };
  if (key === "programmatic" || key === "programmatic / multi-channel") {
    return { src: displayLogo, alt: raw };
  }

  const channelKey = Object.entries(CHANNEL_LABELS).find(([, label]) => label.toLowerCase() === key)?.[0];
  if (channelKey && CHANNEL_HERO_ASSETS[channelKey]) {
    return { src: CHANNEL_HERO_ASSETS[channelKey], alt: CHANNEL_LABELS[channelKey] || raw };
  }

  return null;
};

const formatDateLabel = (value) => {
  const formatted = formatFlightDateShort(value);
  return formatted === "—" ? "" : formatted;
};

const absoluteApiUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = API_BASE_URL;
  if (!base) return raw.startsWith("/") ? raw : `/${raw}`;
  return `${base}${raw.startsWith("/") ? raw : `/${raw}`}`;
};

const normalizeAdvertiserLogoMeta = (record = {}) => {
  const logoUrl = String(record.logoUrl ?? record.logo_url ?? "").trim();
  const logoDriveFileId = String(
    record.logoDriveFileId ?? record.logo_drive_file_id ?? ""
  ).trim();
  const logoUpdatedAt = record.logoUpdatedAt ?? record.logo_updated_at ?? "";
  const logoAltText = String(record.logoAltText ?? record.logo_alt_text ?? "").trim();
  const explicitHasLogo =
    typeof record.hasLogo === "boolean" ? record.hasLogo : undefined;

  return {
    ...record,
    logoUrl,
    logoDriveFileId,
    logoUpdatedAt,
    logoAltText,
    hasLogo:
      typeof explicitHasLogo === "boolean"
        ? explicitHasLogo
        : Boolean(record.advertiserId || logoDriveFileId || logoUrl),
  };
};

const appendLogoStamp = (value, stamp) => {
  const base = String(value || "").trim();
  if (!base) return "";
  const version = String(stamp || "").trim();
  if (!version) return base;
  return `${base}${base.includes("?") ? "&" : "?"}v=${encodeURIComponent(version)}`;
};

const buildAdvertiserLogoCandidates = ({
  advertiserId,
  logoUrl,
  logoDriveFileId,
  logoUpdatedAt,
}) => {
  const normalizedAdvertiserId =
    advertiserId != null && String(advertiserId).trim()
      ? String(advertiserId).trim()
      : "";
  const sources = [];
  const seen = new Set();

  const fallback = normalizedAdvertiserId
    ? appendLogoStamp(
        `${API_BASE_URL}/api/advertisers/${encodeURIComponent(normalizedAdvertiserId)}/logo/file`,
        logoUpdatedAt
      )
    : "";
  const direct = appendLogoStamp(absoluteApiUrl(logoUrl), logoUpdatedAt);

  [fallback, direct].forEach((candidate) => {
    if (!candidate || seen.has(candidate)) return;
    seen.add(candidate);
    sources.push(candidate);
  });

  return sources;
};

const truncateLine = (value, max = 132) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}...`;
};

const formatTimeframeLabel = (timeframe) => {
  if (!timeframe || typeof timeframe !== "object") return "Live window pending";
  /*
   * EDEN speaks this line, so it takes the same governed gate the Delivery
   * chart does: a window label naming a route or service inside the reporting
   * spine is dropped in favour of the window's own dates.
   */
  const governedLabel = sanitizeGovernedWindowLabel(timeframe.label);
  if (governedLabel) return governedLabel;
  if (timeframe.start && timeframe.end) {
    return formatFlightRange(timeframe.start, timeframe.end);
  }
  if (timeframe.datePreset) {
    return String(timeframe.datePreset).replace(/_/g, " ");
  }
  return "Live window pending";
};

const normalizeFlightWindowCandidate = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const window = normalizeFlightWindowCandidate(item);
      if (window?.start && window?.end) return window;
    }
    return null;
  }
  if (typeof value !== "object") return null;

  const nestedCandidates = [
    value.flightWindow,
    value.canonicalFlightWindow,
    value.approvedFlightWindow,
    value.campaignFlightWindow,
    value.campaignWindow,
    value.reportingWindow,
    value.timeframe,
  ].filter((candidate) => candidate && candidate !== value);

  for (const candidate of nestedCandidates) {
    const window = normalizeFlightWindowCandidate(candidate);
    if (window?.start && window?.end) return window;
  }

  const start =
    value.start ||
    value.startDate ||
    value.dateStart ||
    value.date_start ||
    value.flightStart ||
    value.flight_start ||
    value.flightStartDate ||
    value.campaignStartDate ||
    value.campaign_start_date;
  const end =
    value.end ||
    value.endDate ||
    value.dateEnd ||
    value.date_end ||
    value.flightEnd ||
    value.flight_end ||
    value.flightEndDate ||
    value.campaignEndDate ||
    value.campaign_end_date;

  return start && end ? { start, end } : null;
};

const resolveFirstFlightWindow = (candidates = []) => {
  for (const candidate of candidates) {
    const window = normalizeFlightWindowCandidate(candidate);
    if (window?.start && window?.end) return window;
  }
  return null;
};

const isPendingFlightText = (value) =>
  /\b(?:awaiting|pending|not available|unavailable|syncing)\b/i.test(String(value || ""));

export const buildFlightContext = ({ timeframe, toplineData }) => {
  const contract = String(toplineData?.raw?.sourceContract?.contract || "")
    .trim()
    .toLowerCase();
  const raw = toplineData?.raw || {};
  const canonicalFlightWindow =
    contract === CANONICAL_REPORTING_CONTRACT
      ? resolveFirstFlightWindow([
          raw.flightWindow,
          raw.canonicalFlightWindow,
          raw.canonical?.flightWindow,
          raw.canonicalSummary?.flightWindow,
          raw.canonicalReporting?.flightWindow,
        ])
      : null;

  if (canonicalFlightWindow?.start && canonicalFlightWindow?.end) {
    return {
      label: "Current Flight",
      value: formatFlightRange(canonicalFlightWindow.start, canonicalFlightWindow.end),
    };
  }

  const campaignFlightWindow = resolveFirstFlightWindow([
    raw.approvedFlightWindow,
    raw.approvedFlight,
    raw.approvedCampaign,
    raw.campaign,
    raw.activeCampaign,
    raw.currentCampaign,
    raw.campaignFlightWindow,
    raw.flightWindow,
    {
      start: raw.flightStart,
      end: raw.flightEnd,
    },
  ]);

  if (campaignFlightWindow?.start && campaignFlightWindow?.end) {
    return {
      label: "Flight",
      value: formatFlightRange(campaignFlightWindow.start, campaignFlightWindow.end),
    };
  }

  const reportingWindow = resolveFirstFlightWindow([
    timeframe,
    raw.reportingWindow,
    raw.timeframe,
    raw.displayTimeframe,
    {
      start: raw.startDate,
      end: raw.endDate,
    },
    {
      start: raw.requestedStartDate,
      end: raw.requestedEndDate,
    },
  ]);

  if (reportingWindow?.start && reportingWindow?.end) {
    return {
      label: "Flight",
      value: formatFlightRange(reportingWindow.start, reportingWindow.end),
    };
  }

  const pacingMeta = String(toplineData?.pacingMeta || "").trim();
  if (pacingMeta && !isPendingFlightText(pacingMeta)) {
    return {
      label: "Flight",
      value: pacingMeta,
    };
  }

  return {
    label: "Flight",
    value: "Awaiting Flight Window",
  };
};

const buildAdvertiserFallbackLabel = (name) => {
  const cleanName = String(name || "").trim();
  if (!cleanName) return "AD";
  const parts = cleanName.split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || cleanName.slice(0, 2).toUpperCase();
};

const CLIENT_SAFE_CONTEXT_LABELS = {
  campaign_strategy_microsite: "campaign strategy context",
  media_strategy: "approved media planning",
  platform_allocation: "platform allocation modeling",
  film_positioning: "theatrical positioning guidance",
  client_safe_strategy_answers: "client-ready strategy answers",
};

const INTERNAL_CONTEXT_IDENTIFIER_PATTERN = /\b[a-z][a-z0-9]*_[a-z0-9_]*\b/;

export const sanitizeEdenClientFacingText = (value = "") =>
  String(value || "")
    .replace(/\bcampaign_strategy_microsite\b/gi, "campaign strategy context")
    .replace(/\bmedia_strategy\b/gi, "approved media planning")
    .replace(/\bplatform_allocation\b/gi, "platform allocation modeling")
    .replace(/\bfilm_positioning\b/gi, "theatrical positioning guidance")
    .replace(/\bclient_safe_strategy_answers\b/gi, "client-ready strategy answers")
    .replace(/\b[a-z][a-z0-9]*_[a-z0-9_]*\b/g, "campaign context");

export const buildClientSafeContextDescriptors = (capabilities = []) => {
  const seen = new Set();
  return (Array.isArray(capabilities) ? capabilities : [])
    .map((capability) => {
      const raw = String(capability || "").trim();
      const label = CLIENT_SAFE_CONTEXT_LABELS[raw] || "";
      return label && !INTERNAL_CONTEXT_IDENTIFIER_PATTERN.test(label) ? label : "";
    })
    .filter((label) => {
      if (!label || seen.has(label)) return false;
      seen.add(label);
      return true;
    });
};

const joinReadableList = (items = []) => {
  const list = items.filter(Boolean);
  if (list.length <= 1) return list[0] || "";
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")}, and ${list[list.length - 1]}`;
};

export const buildPageAssistantIntro = (pageAwareness) => {
  const pageTitle = String(pageAwareness?.pageTitle || "").trim();
  const capabilities = Array.isArray(pageAwareness?.pageCapabilities)
    ? pageAwareness.pageCapabilities.filter(Boolean)
    : [];
  const descriptors = buildClientSafeContextDescriptors(capabilities);

  if (!pageTitle && !capabilities.length) {
    return "Ask Eden by text for campaign insight, strategy, or platform performance context.";
  }

  if (!descriptors.length) {
    return `Eden is scoped to ${pageTitle || "this page"} right now.`;
  }

  return `${joinReadableList(descriptors.slice(0, 4))} ${
    /microsite/i.test(pageTitle) ? "are active for this microsite" : "are active for this page"
  }.`;
};

const buildEdenHeaderLabel = (pageTitle, platformLabel) => {
  const resolved = String(pageTitle || platformLabel || "Analytics").trim();
  return resolved ? resolved.toUpperCase() : "ANALYTICS";
};

const EMPTY_TOP_PLATFORM_LABEL = "Awaiting launch signals";

const normalizeEdenToplineCopy = (value = "") =>
  String(value || "")
    .replace(/\bCross Channel\b/g, "Cross Platform")
    .replace(/\bcross-channel\b/g, "cross-platform")
    .replace(/\bCross-channel\b/g, "Cross-platform");

const parseToplinePercentValue = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const match = String(value || "").match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const numeric = Number(match[0]);
  return Number.isFinite(numeric) ? numeric : null;
};

const extractToplinePercentValues = (value = "") =>
  Array.from(String(value || "").matchAll(/-?\d+(?:\.\d+)?\s*%/g))
    .map((match) => parseToplinePercentValue(match[0]))
    .filter((value) => Number.isFinite(value));

const valuesMatchWithinToplineTolerance = (left, right) =>
  Number.isFinite(left) && Number.isFinite(right) && Math.abs(left - right) <= 0.05;

const collectPlatformCtrSources = (toplineData = null, pageAwareness = null) => {
  const sources = [
    toplineData?.platforms,
    toplineData?.platformRows,
    toplineData?.raw?.platforms,
    toplineData?.raw?.platformRows,
    toplineData?.raw?.platformBreakdown,
    toplineData?.raw?.platformMetrics,
    pageAwareness?.metadata?.platformToplineRows,
    pageAwareness?.metadata?.platformMetrics,
  ];

  return sources.flatMap((source) => {
    const rows = Array.isArray(source)
      ? source
      : source && typeof source === "object"
      ? Object.entries(source).map(([key, value]) =>
          value && typeof value === "object" ? { platform: key, ...value } : { platform: key, ctr: value }
        )
      : [];

    return rows
      .map((row) => {
        const label = String(row?.label || row?.platformLabel || row?.platform || row?.name || "").trim();
        const ctr = parseToplinePercentValue(row?.ctr ?? row?.clickThroughRate ?? row?.blendedCtr);
        return label && Number.isFinite(ctr) ? { label, ctr } : null;
      })
      .filter(Boolean);
  });
};

const hasUnsupportedToplineClaim = (value = "") =>
  /generating efficient traffic at scale|click response holding strong/i.test(String(value || ""));

const EDEN_TOPLINE_BLOCKED_ANALYSIS_LANGUAGE =
  /strong current traffic read|response and cost efficiency moving in the same direction|early interest signal|conversion capture should (?:stay|remain) the watchpoint|monitoring whether added spend begins to dilute response quality|verified delivery signals|downstream intent|blanket platform win|no downstream action field|in this read|should stay anchored|before making lower-funnel claims|source-of-truth|KPI read|decision baseline|current traffic read|cross-channel|\bcanonical\b|\bfallback\b|\bresolver\b|\braw\b|raw spend|\bsnapshot\b|\bcache\b|\brollup\b|\bpayload\b|\bcontract\b/i;

const hasToplineKpiSummaryLanguage = (value = "") => {
  const text = String(value || "").toLowerCase();
  const summaryMarkers = [
    /currently leading delivery/,
    /\bspend is\b/,
    /\bctr is\b/,
    /\btracked conversions are\b/,
    /\bengagements are\b/,
    /\bvideo views are\b/,
    /\bpacing is currently\b/,
  ];
  return summaryMarkers.filter((marker) => marker.test(text)).length >= 2;
};

export const isEdenToplineAnalysisGrounded = (analysis, toplineData = null, pageAwareness = null) => {
  if (hasUnsupportedToplineClaim(analysis)) return false;
  if (EDEN_TOPLINE_BLOCKED_ANALYSIS_LANGUAGE.test(String(analysis || ""))) return false;
  const percentValues = extractToplinePercentValues(analysis);
  if (!percentValues.length || !toplineData) return true;

  const blendedCtr = parseToplinePercentValue(toplineData?.ctr ?? toplineData?.raw?.ctr);
  const platformCtrSources = collectPlatformCtrSources(toplineData, pageAwareness);
  const analysisText = String(analysis || "").toLowerCase();

  return percentValues.every((percentValue) => {
    if (valuesMatchWithinToplineTolerance(percentValue, blendedCtr)) return true;
    return platformCtrSources.some((source) => {
      const platformMentioned = analysisText.includes(source.label.toLowerCase());
      return platformMentioned && valuesMatchWithinToplineTolerance(percentValue, source.ctr);
    });
  });
};

const parseToplineNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value === null || value === undefined) return null;
  const raw = String(value || "").trim();
  if (!raw || raw === "—") return null;
  const numeric = Number(raw.replace(/[$,%\s,]/g, ""));
  return Number.isFinite(numeric) ? numeric : null;
};

const formatEdenToplineInteger = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return Math.round(numeric).toLocaleString("en-US");
};

const formatEdenToplinePercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return `${numeric.toFixed(2)}%`;
};

const buildEdenToplineProof = ({ impressions, clicks, ctr, videoViews }) => {
  const proof = [];
  if (Number.isFinite(impressions) && impressions > 0) {
    proof.push(`${formatEdenToplineInteger(impressions)} impressions`);
  }
  if (Number.isFinite(clicks) && clicks > 0) {
    proof.push(`${formatEdenToplineInteger(clicks)} clicks`);
  }
  if (Number.isFinite(ctr) && ctr > 0) {
    proof.push(`${formatEdenToplinePercent(ctr)} blended CTR`);
  }
  if (proof.length < 3 && Number.isFinite(videoViews) && videoViews > 0) {
    proof.push(`${formatEdenToplineInteger(videoViews)} video views`);
  }
  if (proof.length >= 3) return `${proof[0]}, ${proof[1]}, and ${proof[2]}`;
  if (proof.length === 2) return `${proof[0]} and ${proof[1]}`;
  return proof[0] || "";
};

const buildEdenToplinePlatformRole = (platform, vertical) => {
  const label = String(platform || "").trim();
  if (!label) return "";
  const key = label.toLowerCase();
  if (vertical === "film" && key.includes("youtube")) {
    return `${label} is carrying the primary video discovery role, making it the clearest place to read trailer engagement and audience attention.`;
  }
  if (vertical === "film" && key.includes("ctv")) {
    return `${label} is carrying the premium reach role, making it the clearest place to read release-window awareness.`;
  }
  if (key.includes("youtube")) {
    return `${label} is carrying the main video and discovery role, making it a strong source of creative learning.`;
  }
  if (key.includes("ctv")) {
    return `${label} is carrying the main reach role, making it the clearest scale lever for awareness.`;
  }
  if (key.includes("meta") || key.includes("facebook") || key.includes("instagram")) {
    return `${label} is contributing the main social response role, making audience and creative pairing the priority read.`;
  }
  return `${label} is carrying the main delivery role surfaced here, making it the first platform to review for creative, audience, and placement learnings.`;
};

const buildEdenToplineNextStep = ({ vertical, hasConversions, hasRetailOutcome, hasAutomotiveLeads }) => {
  if (hasConversions) {
    return "The next optimization focus should be protecting the audience, creative, and placement combinations producing efficient conversions while trimming weaker response paths.";
  }
  if (hasRetailOutcome) {
    return "The next optimization focus should be protecting the audience, creative, and placement combinations producing efficient response while trimming weaker paths.";
  }
  if (hasAutomotiveLeads) {
    return "The next optimization focus should be separating the market, audience, and creative combinations producing the highest-quality shopper response.";
  }
  if (vertical === "film") {
    return "The next optimization focus should be isolating the creative and audience pairings producing the strongest discovery and carrying those learnings into the next title push.";
  }
  if (vertical === "automotive") {
    return "The next optimization focus should be identifying which creative, audience, and market combinations are producing the most efficient shopper traffic.";
  }
  if (vertical === "retail") {
    return "The next optimization focus should be identifying which product messages, audiences, and placements are producing the most efficient traffic.";
  }
  if (vertical === "travel_hospitality") {
    return "The next optimization focus should be identifying which destination messages, origin markets, and placements are producing the most efficient traveler-intent traffic.";
  }
  return "The next optimization focus should be identifying which creative, audience, and placement combinations are producing the most efficient clicks.";
};

const resolveEdenToplineVertical = (toplineData = null, pageAwareness = null) =>
  normalizeAdvertiserVertical(
    toplineData?.raw?.advertiserVertical ||
      toplineData?.raw?.advertiser?.vertical ||
      toplineData?.raw?.vertical ||
      pageAwareness?.advertiserVertical ||
      pageAwareness?.metadata?.advertiserVertical ||
      pageAwareness?.advertiser?.vertical
  );

const buildDeterministicEdenToplineAnalysis = (toplineData = null, pageAwareness = null) => {
  const label = String(toplineData?.scopeLabel || toplineData?.advertiserName || "This campaign").trim();
  const vertical = resolveEdenToplineVertical(toplineData, pageAwareness);
  const topPlatform = String(toplineData?.topPlatform || "").trim();
  const spend = parseToplineNumber(toplineData?.spend ?? toplineData?.raw?.spend);
  const impressions = parseToplineNumber(toplineData?.impressions ?? toplineData?.raw?.impressions);
  const clicks = parseToplineNumber(toplineData?.clicks ?? toplineData?.raw?.clicks);
  const ctr = parseToplineNumber(toplineData?.ctr ?? toplineData?.raw?.ctr);
  const cpc = parseToplineNumber(toplineData?.cpc_or_cpa ?? toplineData?.raw?.cpc);
  const conversions = parseToplineNumber(toplineData?.conversions ?? toplineData?.raw?.conversions);
  const videoViews = parseToplineNumber(
    toplineData?.raw?.trueViewViews ??
      toplineData?.raw?.true_view_views ??
      toplineData?.raw?.videoViews ??
      toplineData?.raw?.video_views
  );
  const leads = parseToplineNumber(toplineData?.raw?.leads ?? toplineData?.raw?.leadForms);
  const roas = parseToplineNumber(toplineData?.raw?.roas);
  const cartActivity = parseToplineNumber(toplineData?.raw?.cartActivity ?? toplineData?.raw?.cart_additions);
  const hasConversions = Number.isFinite(conversions) && conversions > 0;
  const hasAutomotiveLeads = Number.isFinite(leads) && leads > 0;
  const hasRetailOutcome = Number.isFinite(roas) || Number.isFinite(cartActivity);
  const proof = buildEdenToplineProof({ impressions, clicks, ctr, videoViews });
  const proofClause = proof ? `, with ${proof}` : "";
  const sentences = [];

  if (vertical === "film" && (Number.isFinite(videoViews) || Number.isFinite(impressions))) {
    sentences.push(
      `${label} is delivering theatrical awareness and traffic performance${proofClause}.`
    );
  } else if (vertical === "automotive" && hasAutomotiveLeads) {
    sentences.push(
      `${label} is showing automotive consideration and lead response${proofClause}.`
    );
  } else if (vertical === "automotive") {
    sentences.push(
      `${label} is reading as an automotive consideration push, with traffic quality and media efficiency guiding the shopper-engagement read${proofClause}.`
    );
  } else if (vertical === "retail" && (hasRetailOutcome || hasConversions)) {
    sentences.push(
      `${label} is showing retail performance momentum${proofClause}.`
    );
  } else if (vertical === "retail") {
    sentences.push(
      `${label} is reading as a retail media engagement push, with reach and click efficiency guiding product-message and audience learning${proofClause}.`
    );
  } else if (vertical === "travel_hospitality") {
    sentences.push(
      `${label} is reading as a travel and hospitality demand push, with reach and click efficiency guiding destination-message and origin-market learning${proofClause}.`
    );
  } else if (Number.isFinite(ctr) && Number.isFinite(cpc) && ctr >= 2 && cpc <= 1) {
    sentences.push(
      `${label} is showing efficient cross-platform engagement${proofClause}.`
    );
  } else if (Number.isFinite(ctr) && ctr < 1) {
    sentences.push(
      `${label} is building reach ahead of click response${proofClause}, making audience and placement selectivity the immediate opportunity.`
    );
  } else if (Number.isFinite(impressions) && Number.isFinite(clicks)) {
    sentences.push(
      `${label} is generating a usable cross-platform performance read${proofClause}.`
    );
  } else if (Number.isFinite(spend) || Number.isFinite(impressions)) {
    sentences.push(
      `${label} has live delivery to evaluate, with spend and reach ready to guide the next optimization pass.`
    );
  } else {
    sentences.push("Current campaign delivery is still building, so Eden needs more live activity before making a performance read.");
  }

  const platformSentence = buildEdenToplinePlatformRole(topPlatform, vertical);
  if (platformSentence) sentences.push(platformSentence);

  sentences.push(
    buildEdenToplineNextStep({ vertical, hasConversions, hasRetailOutcome, hasAutomotiveLeads })
  );

  return `Brief Analysis: ${sanitizeVerticalIntelligenceCopy(sentences.join(" "), vertical)}`;
};

const hasToplineDeliveryActivity = (toplineData = null) => {
  const raw = toplineData?.raw || {};
  return [raw.spend, raw.impressions, raw.clicks, raw.conversions, toplineData?.spend, toplineData?.impressions].some(
    (value) => {
      const numeric = Number(String(value || "").replace(/[$,%\s,]/g, ""));
      return Number.isFinite(numeric) && numeric > 0;
    }
  );
};

export const buildGroundedEdenToplineAnalysis = ({
  registeredToplineAnalysis = "",
  toplineData = null,
  pageAwareness = null,
} = {}) => {
  const cleanRegistered = String(registeredToplineAnalysis || "").trim();
  if (
    cleanRegistered &&
    !EDEN_TOPLINE_BLOCKED_ANALYSIS_LANGUAGE.test(cleanRegistered) &&
    !hasToplineKpiSummaryLanguage(cleanRegistered) &&
    isEdenToplineAnalysisGrounded(cleanRegistered, toplineData, pageAwareness)
  ) {
    return normalizeEdenToplineCopy(cleanRegistered);
  }

  if (!toplineData || !hasToplineDeliveryActivity(toplineData)) {
    return "Brief Analysis: Current campaign delivery is still building; Eden will ground the analysis once current KPI activity is available.";
  }

  const deterministic = buildDeterministicEdenToplineAnalysis(toplineData, pageAwareness);
  return EDEN_TOPLINE_BLOCKED_ANALYSIS_LANGUAGE.test(deterministic)
    ? "Brief Analysis: Current campaign delivery is active, with spend, reach, clicks, and efficiency ready to guide the next optimization pass."
    : deterministic;
};

const buildMetricsFromFetchedTopline = (toplineData, { platformLabel = "", isPlatformScoped = false } = {}) => {
  if (!toplineData) return [];
  const raw = toplineData.raw || {};
  const spendIsBilled = raw?.spendBasis === "client_billed";

  const metrics = [
    toplineData?.spend
      ? {
          label: spendIsBilled ? "Billed Spend" : "Spend",
          value: toplineData.spend,
          meta: spendIsBilled
            ? isPlatformScoped
              ? `${platformLabel || "Platform"} billed delivery`
              : "Client-facing delivery"
            : isPlatformScoped
            ? `${platformLabel || "Platform"} delivery`
            : raw?.snapshotSource || "Current delivery",
        }
      : null,
    toplineData?.impressions
      ? {
          label: "Impressions",
          value: toplineData.impressions,
          meta: isPlatformScoped ? "Platform reach" : "Latest delivery window",
        }
      : null,
    raw?.clicks != null
      ? {
          label: "Clicks",
          value: Math.round(Number(raw.clicks || 0)).toLocaleString("en-US"),
          meta: isPlatformScoped ? "Platform clicks" : "Current click signal",
        }
      : null,
    toplineData?.ctr
      ? {
          label: isPlatformScoped ? "CTR" : CANONICAL_CTR_LABEL,
          value: toplineData.ctr,
          meta: isPlatformScoped ? "Platform CTR" : "Cross-platform blended CTR",
        }
      : null,
    toplineData?.cpc_or_cpa
      ? {
          label: toplineData.costLabel || "CPC",
          value: toplineData.cpc_or_cpa,
          meta: isPlatformScoped ? "Platform cost efficiency" : "Efficiency signal",
        }
      : null,
    raw?.cpm != null
      ? {
          label: spendIsBilled ? "Billed CPM" : "CPM",
          value: `$${Number(raw.cpm || 0).toFixed(2)}`,
          meta: spendIsBilled
            ? "Client-facing impression efficiency"
            : "Impression efficiency",
        }
      : null,
    raw?.engagements != null
      ? {
          label: "Engagements",
          value: Math.round(Number(raw.engagements || 0)).toLocaleString("en-US"),
          meta: "Platform engagement signal",
        }
      : null,
    (raw?.trueViewViews ?? raw?.true_view_views ?? raw?.videoViews) != null
      ? {
          label: "Video Views",
          value: Math.round(
            Number(raw.trueViewViews ?? raw.true_view_views ?? raw.videoViews ?? 0)
          ).toLocaleString("en-US"),
          meta: "Platform video signal",
        }
      : null,
    raw?.conversions != null
      ? {
          label: "Conversions",
          value: Math.round(Number(raw.conversions || 0)).toLocaleString("en-US"),
          meta: "Observed conversion signal",
        }
      : null,
    raw?.sessions != null
      ? {
          label: "Sessions",
          value: Math.round(Number(raw.sessions || 0)).toLocaleString("en-US"),
          meta: "GA support signal",
        }
      : null,
    !isPlatformScoped && toplineData?.topPlatform
      ? {
          label: "Top Platform",
          value: toplineData.topPlatform,
          meta: "Current Eden scope",
          hero: resolveTopPlatformHero(toplineData.topPlatform),
        }
      : null,
    toplineData?.pacingStatus
      ? {
          label: isPlatformScoped ? "Status" : "Pacing Status",
          value: toplineData.pacingStatus,
          meta: toplineData.pacingMeta || "Current flight context",
        }
      : null,
  ].filter(Boolean);

  return metrics.slice(0, 6);
};

const GlobalVoiceControllerSession = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const edenContext = useEdenContext();
  const pageAwarenessState = usePageAwarenessState();
  const pageAwareness = useMemo(() => {
    if (pageAwarenessState && typeof pageAwarenessState === "object") {
      return pageAwarenessState.pageAwareness || {};
    }
    return {};
  }, [pageAwarenessState]);

  const {
    isListening,
    isSending,
    isThinking,
    isSpeaking,
    voicePhase,
    interimTranscript,
    lastUserUtterance,
    lastAssistantReply,
    startListening,
    stopListening,
    handleFinalTranscript,
    setEdenContext,
    setLastAssistantReply,
    greetUser,
    setVoicePhase,
    setInterimTranscript,
    clearInterimTranscript,
    voiceOutputStatus,
    clearVoiceOutputStatus,
    lastAssistantVisualizations,
    setLastAssistantVisualizations,
    delegatedWork,
    setDelegatedWork,
    conversationTurns: providedConversationTurns,
    lastGovernedChange,
    isAwaitingReply = false,
    isAnswerToEdenQuestion,
  } = useSherlocVoice();
  const conversationTurns = Array.isArray(providedConversationTurns) ? providedConversationTurns : [];

  const voiceInputControllerRef = useRef(null);
  const textInputRef = useRef(null);
  const textPanelRef = useRef(null);
  const textPanelBodyRef = useRef(null);
  const [isTextPanelOpen, setIsTextPanelOpen] = useState(false);
  // Full-screen conversation: the same panel, same conversation, same draft,
  // laid out as a sheet over the page (see useEdenFullscreenSheet).
  const [isEdenFullscreen, setIsEdenFullscreen] = useState(false);
  const isFullscreenSheetOpen = isTextPanelOpen && isEdenFullscreen;
  useEdenFullscreenSheet(isFullscreenSheetOpen, textPanelRef);
  const [isToplineOpen, setIsToplineOpen] = useState(false);
  const [typedPrompt, setTypedPrompt] = useState("");
  const [voiceCommandTranscript, setVoiceCommandTranscript] = useState("");
  const [isVoiceCommandPending, setIsVoiceCommandPending] = useState(false);
  const [toplineData, setToplineData] = useState(null);
  const [isToplineLoading, setIsToplineLoading] = useState(false);
  const [advertiserLogoBroken, setAdvertiserLogoBroken] = useState(false);
  const [voiceInputSnapshot, setVoiceInputSnapshot] = useState(
    INITIAL_VOICE_INPUT_SNAPSHOT
  );
  const [voiceNotice, setVoiceNotice] = useState("");
  // Set once the microphone flow has been used in this session; until then
  // the idle "voice input stopped" status has nothing to report.
  const [voiceInputWasUsed, setVoiceInputWasUsed] = useState(false);
  useEffect(() => {
    if (voiceInputSnapshot.active || voiceInputSnapshot.phase !== "idle") setVoiceInputWasUsed(true);
  }, [voiceInputSnapshot.active, voiceInputSnapshot.phase]);
  const [edenWorkUnread, setEdenWorkUnread] = useState([]);
  const [isStatusPillDismissed, setIsStatusPillDismissed] = useState(false);
  const previousFocusRef = useRef(null);
  const lastInjectedContextRef = useRef("");
  const submittedVoiceSessionRef = useRef(null);
  const voiceCommandRequestRef = useRef(0);
  const minimizeAfterVoiceCommandRef = useRef(false);
  const typedDraftBeforeVoiceRef = useRef("");

  const logVoiceState = useCallback((event, detail = {}) => {
    // Production telemetry is metadata-only: spoken/typed content is reduced
    // to character counts before it can reach the console.
    console.log("[EdenVoice][Client]", event, sanitizeVoiceLogDetail(detail));
  }, []);

  const getVoiceRuntimeDiagnostics = useCallback(() => {
    const speechRecognitionSupported = Boolean(
      window?.SpeechRecognition || window?.webkitSpeechRecognition
    );
    const speechSynthesisSupported = Boolean(window?.speechSynthesis);
    const browserVoicesLoaded = speechSynthesisSupported
      ? (window.speechSynthesis.getVoices?.() || []).length
      : 0;
    return {
      speechRecognitionSupported,
      microphoneApiSupported: Boolean(navigator?.mediaDevices?.getUserMedia),
      speechSynthesisSupported,
      browserVoicesLoaded,
      voiceOutputStatus,
    };
  }, [voiceOutputStatus]);

  // One coherent close path for the conversation panel. Closing:
  //  - stops any active or permission-pending voice capture,
  //  - releases microphone resources,
  //  - unmounts the panel + composer (see the render guard below),
  //  - restores keyboard focus to the element that opened the panel.
  // Conversation history lives in the voice provider, so closing never
  // deletes it, and an in-flight text request continues server-side.
  // Back from the full-screen sheet (and navigating away from it) passes
  // keepPreview: capture still stops and the mic is still released, but the
  // conversation stays one tap away in the compact preview.
  const closeTextPanel = useCallback((options) => {
    const keepPreview = options?.keepPreview === true;
    if (voiceInputControllerRef.current?.isActive()) {
      logVoiceState("panel_close_stops_listening");
    }
    voiceInputControllerRef.current?.stop("panel_close");
    clearInterimTranscript();
    // Complete visual dismissal: clear transient voice notices and any
    // recognition-error presentation, and suppress the detached status pill
    // until new activity — conversation history and delegated-work state
    // are intentionally preserved.
    setVoiceNotice("");
    if (voicePhase === "error") {
      setVoicePhase("idle");
    }
    if (!keepPreview) setIsStatusPillDismissed(true);
    setIsTextPanelOpen(false);
    setIsEdenFullscreen(false);
    const previousFocus = previousFocusRef.current;
    previousFocusRef.current = null;
    if (previousFocus && typeof previousFocus.focus === "function" && document.contains(previousFocus)) {
      window.setTimeout(() => previousFocus.focus(), 0);
    }
  }, [voicePhase, clearInterimTranscript, logVoiceState, setVoicePhase]);

  const minimizeTextPanel = useCallback(() => closeTextPanel({ keepPreview: true }), [closeTextPanel]);

  const executeEdenActionIntent = useCallback(
    (transcript, source = "voice") => {
      const actionIntent = resolveEdenActionIntent(transcript);
      if (!actionIntent || actionIntent.type === EDEN_ACTION_TYPES.ANALYSIS_FALLBACK) {
        return false;
      }

      const confirmation =
        actionIntent.type === EDEN_ACTION_TYPES.NOOP_UNAVAILABLE
          ? actionIntent.unavailableMessage || "That action is not available for this account."
          : actionIntent.message || actionIntent.confirmation || `Opening ${actionIntent.label}.`;

      setLastAssistantReply(confirmation);
      setLastAssistantVisualizations([]);
      // The confirmation is Eden's reply line; it is not repeated as a notice.
      setVoiceNotice("");
      logVoiceState("eden_action_resolved", {
        source,
        actionType: actionIntent.type,
        actionId: actionIntent.id || null,
        route: actionIntent.route || null,
      });

      if (actionIntent.type === EDEN_ACTION_TYPES.NAVIGATE_ROUTE && actionIntent.route) {
        navigate(actionIntent.route);
        if (source === "voice") {
          minimizeAfterVoiceCommandRef.current = true;
        }
        return true;
      }

      if (actionIntent.type === EDEN_ACTION_TYPES.OPEN_EXTERNAL && actionIntent.url) {
        window.open(actionIntent.url, "_blank", "noopener,noreferrer");
        if (source === "voice") {
          minimizeAfterVoiceCommandRef.current = true;
        }
        return true;
      }

      return true;
    },
    [logVoiceState, navigate, setLastAssistantReply, setLastAssistantVisualizations]
  );

  const submitVoiceTranscript = useCallback(
    async (transcript, source = "voice") => {
      const cleanTranscript = String(transcript || "").trim();
      if (!cleanTranscript) return;

      if (source === "voice") {
        const draftToRestore = typedDraftBeforeVoiceRef.current;
        window.setTimeout(() => {
          setTypedPrompt((current) =>
            current === cleanTranscript ? draftToRestore : current
          );
          typedDraftBeforeVoiceRef.current = "";
        }, 0);
      } else {
        setTypedPrompt("");
      }
      setIsStatusPillDismissed(false);
      if (prefersFullscreenEdenChat()) setIsEdenFullscreen(true);
      setIsTextPanelOpen(true);
      clearVoiceOutputStatus?.();
      // No narration: the working indicator in Eden's reply slot shows the
      // request is being answered. Clear any notice left from before.
      setVoiceNotice("");
      window.setTimeout(() => {
        textInputRef.current?.focus?.();
      }, 0);
      logVoiceState("voice_transcript_submitting", {
        source,
        transcript: cleanTranscript,
      });

      // Delegated planning briefs bypass local action routing so a pasted
      // RFP naming a platform can never be hijacked into navigation — the
      // durable backend delegation path must receive it.
      const delegationCandidate = looksLikeDelegatedPlanningRequest(cleanTranscript);
      // A campaign date change goes to Eden as a governed request, the same
      // way for typed, dictated and Eden Entry text: no local shortcut.
      if (looksLikeCampaignScheduleEditRequest(cleanTranscript)) {
        await handleFinalTranscript(cleanTranscript, { delegationCandidate, scheduleEditCandidate: true });
        return;
      }
      // An answer to Eden's own question ("Which campaign do you mean?") goes
      // to Eden even when it is also a page name ("YouTube").
      const answersEdenQuestion =
        Boolean(isAnswerToEdenQuestion?.(cleanTranscript)) || looksLikeCampaignScheduleQuestion(cleanTranscript);
      if (!delegationCandidate && !answersEdenQuestion && executeEdenActionIntent(cleanTranscript, source)) {
        return;
      }

      await handleFinalTranscript(cleanTranscript, { delegationCandidate });
    },
    [clearVoiceOutputStatus, executeEdenActionIntent, handleFinalTranscript, isAnswerToEdenQuestion, logVoiceState]
  );

  const submitEdenEntryPrompt = useCallback(
    async (prompt) => {
      const cleanPrompt = String(prompt || "").trim();
      if (!cleanPrompt) return;

      setTypedPrompt("");
      setIsStatusPillDismissed(false);
      if (prefersFullscreenEdenChat()) setIsEdenFullscreen(true);
      setIsTextPanelOpen(true);
      clearVoiceOutputStatus?.();
      setLastAssistantVisualizations([]);
      // No narration (see submitVoiceTranscript); clear any earlier notice.
      setVoiceNotice("");
      window.setTimeout(() => {
        textInputRef.current?.focus?.();
      }, 0);
      logVoiceState("eden_entry_prompt_submitting", {
        source: "dashboard_eden_entry",
        transcript: cleanPrompt,
      });

      await handleFinalTranscript(cleanPrompt, { entryPoint: "command_modal" });
    },
    [clearVoiceOutputStatus, handleFinalTranscript, logVoiceState, setLastAssistantVisualizations]
  );

  useEffect(() => {
    const handleEdenEntryPrompt = (event) => {
      submitEdenEntryPrompt(event?.detail?.prompt);
    };

    window.addEventListener(EDEN_ENTRY_PROMPT_EVENT, handleEdenEntryPrompt);
    return () => window.removeEventListener(EDEN_ENTRY_PROMPT_EVENT, handleEdenEntryPrompt);
  }, [submitEdenEntryPrompt]);

  const currentPath =
    String(location?.pathname || "")
      .toLowerCase()
      .replace(/\/+$/, "") || "/";

  const effectivePageAwareness = useMemo(() => {
    const advertiserId =
      pageAwareness?.advertiserId ??
      edenContext?.advertiser?.id ??
      null;
    const advertiserName =
      pageAwareness?.advertiserName ||
      edenContext?.advertiser?.name ||
      "";
    const campaignId = pageAwareness?.campaignId ?? null;
    const campaignName = pageAwareness?.campaignName || "";
    const timeframe = pageAwareness?.timeframe && typeof pageAwareness.timeframe === "object"
      ? pageAwareness.timeframe
      : edenContext?.dateRange
      ? {
          start: edenContext.dateRange.start || null,
          end: edenContext.dateRange.end || null,
          label:
            edenContext.dateRange.start && edenContext.dateRange.end
              ? `${formatDateLabel(edenContext.dateRange.start)} - ${formatDateLabel(edenContext.dateRange.end)}`
              : "Live window pending",
        }
      : null;

    return {
      routeKey: pageAwareness?.routeKey || currentPath,
      routePath: pageAwareness?.routePath || currentPath,
      pageTitle: pageAwareness?.pageTitle || edenContext?.page || "Current Page",
      pageType: pageAwareness?.pageType || "page",
      advertiserId,
      advertiserName,
      campaignId,
      campaignName,
      timeframe,
      platformKey: pageAwareness?.platformKey || edenContext?.channel || "",
      platformLabel:
        pageAwareness?.platformLabel ||
        CHANNEL_LABELS[String(pageAwareness?.platformKey || edenContext?.channel || "").trim()] ||
        "",
      toplineScope:
        pageAwareness?.toplineScope === "page" || pageAwareness?.toplineScope === "aggregate"
          ? pageAwareness.toplineScope
          : Array.isArray(pageAwareness?.pageToplineMetrics) && pageAwareness.pageToplineMetrics.length
          ? "page"
          : "aggregate",
      sectionKey: pageAwareness?.sectionKey || "",
      sectionLabel: pageAwareness?.sectionLabel || "",
      pageCapabilities: Array.isArray(pageAwareness?.pageCapabilities)
        ? pageAwareness.pageCapabilities
        : [],
      pageToplineMetrics: Array.isArray(pageAwareness?.pageToplineMetrics)
        ? pageAwareness.pageToplineMetrics
        : Array.isArray(pageAwareness?.toplineMetrics)
        ? pageAwareness.toplineMetrics
        : [],
      aggregateToplineMetrics: Array.isArray(pageAwareness?.aggregateToplineMetrics)
        ? pageAwareness.aggregateToplineMetrics
        : [],
      toplineAuthority:
        pageAwareness?.toplineAuthority === "shared_fetch"
          ? "shared_fetch"
          : "page",
      metadata: pageAwareness?.metadata && typeof pageAwareness.metadata === "object"
        ? pageAwareness.metadata
        : {},
    };
  }, [currentPath, edenContext, pageAwareness]);

  const mergedEdenContext = useMemo(() => {
    const mergedAdvertiser = {
      ...(edenContext?.advertiser || {}),
      id: effectivePageAwareness.advertiserId ?? edenContext?.advertiser?.id ?? null,
      name: effectivePageAwareness.advertiserName || edenContext?.advertiser?.name || "",
      vertical: normalizeAdvertiserVertical(
        effectivePageAwareness.advertiserVertical ||
          edenContext?.advertiser?.vertical ||
          edenContext?.advertiserVertical
      ),
    };
    mergedAdvertiser.verticalContext = getVerticalIntelligenceConfig(mergedAdvertiser.vertical);

    const timeframe = effectivePageAwareness.timeframe;
    const mergedDateRange =
      timeframe?.start && timeframe?.end
        ? { start: timeframe.start, end: timeframe.end }
        : edenContext?.dateRange || null;
    const dashboardContext = resolveEdenDashboardContextFromPageAwareness(
      effectivePageAwareness,
      edenContext
    );

    return {
      ...(edenContext || {}),
      page: effectivePageAwareness.pageTitle || edenContext?.page || "Current Page",
      pageType: effectivePageAwareness.pageType || edenContext?.pageType || null,
      route: effectivePageAwareness.routePath || edenContext?.route || currentPath,
      routeKey: effectivePageAwareness.routeKey || currentPath,
      channel: effectivePageAwareness.platformKey || edenContext?.channel || null,
      platformLabel: effectivePageAwareness.platformLabel || null,
      sectionKey: effectivePageAwareness.sectionKey || null,
      sectionLabel: effectivePageAwareness.sectionLabel || null,
      advertiser: mergedAdvertiser,
      campaign:
        effectivePageAwareness.campaignId || effectivePageAwareness.campaignName
          ? {
              id: effectivePageAwareness.campaignId ?? null,
              name: effectivePageAwareness.campaignName || "",
            }
          : edenContext?.campaign || null,
      dateRange: mergedDateRange,
      dashboardContext,
      pageCapabilities: effectivePageAwareness.pageCapabilities,
      toplineScope: effectivePageAwareness.toplineScope,
      pageToplineMetrics: effectivePageAwareness.pageToplineMetrics,
      aggregateToplineMetrics: effectivePageAwareness.aggregateToplineMetrics,
      contextType:
        effectivePageAwareness.metadata?.contextType || edenContext?.contextType || null,
      micrositeContext:
        effectivePageAwareness.metadata?.micrositeContext || edenContext?.micrositeContext || null,
      pageAwareness: effectivePageAwareness,
    };
  }, [currentPath, edenContext, effectivePageAwareness]);

  useEffect(() => {
    if (typeof setEdenContext === "function") {
      const signature = JSON.stringify({
        routeKey: mergedEdenContext?.routeKey || null,
        page: mergedEdenContext?.page || null,
        pageType: mergedEdenContext?.pageType || null,
        channel: mergedEdenContext?.channel || null,
        advertiserId: mergedEdenContext?.advertiser?.id || null,
        advertiserName: mergedEdenContext?.advertiser?.name || "",
        advertiserVertical: mergedEdenContext?.advertiser?.vertical || "general",
        advertiserVerticalContext: mergedEdenContext?.advertiser?.verticalContext || null,
        campaignId: mergedEdenContext?.campaign?.id || null,
        campaignName: mergedEdenContext?.campaign?.name || "",
        dateRange: mergedEdenContext?.dateRange || null,
        dashboardContext: mergedEdenContext?.dashboardContext || null,
        pageCapabilities: mergedEdenContext?.pageCapabilities || [],
        toplineScope: mergedEdenContext?.toplineScope || "aggregate",
        pageToplineMetrics: mergedEdenContext?.pageToplineMetrics || [],
        aggregateToplineMetrics: mergedEdenContext?.aggregateToplineMetrics || [],
        contextType: mergedEdenContext?.contextType || null,
        micrositeContext: mergedEdenContext?.micrositeContext || null,
      });

      if (lastInjectedContextRef.current !== signature) {
        lastInjectedContextRef.current = signature;
        setEdenContext(mergedEdenContext);
      }
    }
  }, [mergedEdenContext, setEdenContext]);

  useEffect(() => {
    if (!isTextPanelOpen || voiceInputSnapshot.active) return;
    const timer = window.setTimeout(() => {
      textInputRef.current?.focus?.();
    }, 120);

    return () => window.clearTimeout(timer);
  }, [isTextPanelOpen, voiceInputSnapshot.active]);

  useEffect(() => {
    if (!isTextPanelOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeTextPanel();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isTextPanelOpen, closeTextPanel]);

  useEffect(() => {
    if (!isTextPanelOpen) {
      setIsToplineOpen(false);
      setIsEdenFullscreen(false);
    }
  }, [isTextPanelOpen]);

  // Navigating while the full-screen sheet is open (an Eden action, a next
  // step link, the browser back button) closes the sheet so the new page is
  // visible; the conversation itself is kept.
  const fullscreenRoutePathRef = useRef(null);
  useEffect(() => {
    if (!isFullscreenSheetOpen) {
      fullscreenRoutePathRef.current = null;
      return;
    }
    const path = location?.pathname || "";
    if (fullscreenRoutePathRef.current === null) {
      fullscreenRoutePathRef.current = path;
    } else if (fullscreenRoutePathRef.current !== path) {
      fullscreenRoutePathRef.current = null;
      closeTextPanel({ keepPreview: true });
    }
  }, [isFullscreenSheetOpen, location?.pathname, closeTextPanel]);

  // Keyboard focus stays inside the full-screen sheet (it covers the page).
  useEffect(() => {
    if (!isFullscreenSheetOpen) return undefined;
    const handleTab = (event) => {
      if (event.key !== "Tab") return;
      const sheet = textPanelRef.current;
      if (!sheet) return;
      const focusable = Array.from(sheet.querySelectorAll(EDEN_SHEET_FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!sheet.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, [isFullscreenSheetOpen]);

  useEffect(() => {
    if (isTextPanelOpen && !previousFocusRef.current) {
      previousFocusRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }
  }, [isTextPanelOpen]);

  // React StrictMode intentionally performs a setup -> cleanup -> setup cycle
  // for effects in development. Recreate a controller that was permanently
  // cleaned by that probe; production renders continue to reuse one instance.
  useLayoutEffect(() => {
    let controller = voiceInputControllerRef.current;
    if (!controller || controller.isDestroyed?.()) {
      controller = createVoiceInputSessionController({
        win: window,
        nav: navigator,
      });
      voiceInputControllerRef.current = controller;
    }

    return () => {
      controller.cleanup();
    };
  }, []);

  // Update callbacks without rebuilding the active browser objects. The
  // controller instance, recognizer, and MediaStream remain stable across
  // committed React renders; only these destinations follow the latest render.
  useLayoutEffect(() => {
    voiceInputControllerRef.current?.updateCallbacks({
      onStateChange: (snapshot) => {
        setVoiceInputSnapshot(snapshot);
        if (
          snapshot?.phase === VOICE_INPUT_PHASES.CAPTURED &&
          minimizeAfterVoiceCommandRef.current
        ) {
          minimizeAfterVoiceCommandRef.current = false;
          setIsTextPanelOpen(false);
          setIsStatusPillDismissed(false);
        }
      },
      onDiagnostic: logVoiceState,
      onListeningStart: () => {
        setVoiceNotice("");
        startListening();
        greetUser();
      },
      onListeningStop: () => {
        stopListening();
        setLastAssistantReply((prev) =>
          isEdenGreetingMessage(prev) ? "" : prev
        );
      },
      onInterimTranscript: (transcript) => {
        if (transcript) {
          setInterimTranscript(transcript);
        } else {
          clearInterimTranscript();
        }
      },
      onTranscript: (transcript, { source, sessionId } = {}) => {
        const cleanTranscript = String(transcript || "").trim();
        if (!cleanTranscript) return;

        if (
          sessionId != null &&
          submittedVoiceSessionRef.current === sessionId
        ) {
          logVoiceState("duplicate_voice_command_ignored", {
            sessionId,
            source: source || "voice",
          });
          return;
        }
        if (sessionId != null) {
          submittedVoiceSessionRef.current = sessionId;
        }

        setVoiceCommandTranscript(cleanTranscript);
        setTypedPrompt(cleanTranscript);
        clearInterimTranscript();
        // The voice input status already reads PROCESSING VOICE COMMAND.
        setVoiceNotice("");
        setIsStatusPillDismissed(false);
        setIsTextPanelOpen(true);
        setLastAssistantReply((prev) =>
          isEdenGreetingMessage(prev) ? "" : prev
        );
        logVoiceState("voice_command_handoff", {
          captureSource: source || "voice",
          sessionId: sessionId ?? null,
          transcriptChars: cleanTranscript.length,
        });

        const requestId = voiceCommandRequestRef.current + 1;
        voiceCommandRequestRef.current = requestId;
        setIsVoiceCommandPending(true);
        Promise.resolve(submitVoiceTranscript(cleanTranscript, "voice"))
          .catch(() => {
            if (voiceCommandRequestRef.current !== requestId) return;
            setVoiceNotice(
              "VOICE COMMAND COULD NOT BE SUBMITTED. TAP TALK TO TRY AGAIN, OR CONTINUE BY TYPING."
            );
            logVoiceState("voice_command_submission_failed", {
              sessionId: sessionId ?? null,
            });
          })
          .finally(() => {
            if (voiceCommandRequestRef.current === requestId) {
              setIsVoiceCommandPending(false);
            }
          });
      },
      onError: ({ phase, errorCode }) => {
        setLastAssistantReply((prev) =>
          isEdenGreetingMessage(prev) ? "" : prev
        );
        logVoiceState("voice_input_failure_visible", {
          phase,
          errorCode,
        });
      },
    });
  }, [
    clearInterimTranscript,
    greetUser,
    logVoiceState,
    setInterimTranscript,
    setLastAssistantReply,
    startListening,
    stopListening,
    submitVoiceTranscript,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    console.debug("[EdenVoice][Client][PageAware]", {
      routeKey: effectivePageAwareness.routeKey || currentPath,
      pageType: effectivePageAwareness.pageType || "",
      advertiser: effectivePageAwareness.advertiserName || "",
      campaign: effectivePageAwareness.campaignName || effectivePageAwareness.campaignId || "",
      platformKey: effectivePageAwareness.platformKey || "",
      toplineScope: effectivePageAwareness.toplineScope || "aggregate",
      timeframe: formatTimeframeLabel(effectivePageAwareness.timeframe),
      pageToplineMetricsDetected: Boolean(effectivePageAwareness.pageToplineMetrics?.length),
      aggregateToplineMetricsDetected: Boolean(effectivePageAwareness.aggregateToplineMetrics?.length),
    });
  }, [currentPath, effectivePageAwareness]);

  const shouldHide =
    HIDDEN_ROUTES.includes(currentPath) ||
    currentPath.startsWith("/authorize/return/");

  useEffect(() => {
    if (shouldHide && voiceInputControllerRef.current?.isActive()) {
      voiceInputControllerRef.current.stop("route_hidden");
    }
  }, [shouldHide]);

  useEffect(() => {
    if (voiceOutputStatus?.status !== "failed") return;

    const reason = String(voiceOutputStatus?.reason || "").trim() || "unknown";
    const message =
      reason === "speech_synthesis_unavailable"
        ? "Voice reply is unavailable in this browser right now. Eden's text response is still available."
        : "Eden responded, but spoken playback is unavailable right now. You can still read or type.";

    console.warn("[EdenVoice][Client] voice_output_failed", {
      reason,
      path: voiceOutputStatus?.path || "unknown",
      diagnostics: getVoiceRuntimeDiagnostics(),
    });
    setVoiceNotice(message);
  }, [getVoiceRuntimeDiagnostics, voiceOutputStatus]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    console.log("Eden State:", {
      isListening,
      isSending,
      isThinking,
    });
  }, [isListening, isSending, isThinking]);

  const toggleTextPanel = useCallback(() => {
    if (isTextPanelOpen) {
      setIsTextPanelOpen(false);
      return;
    }
    setIsEdenFullscreen(prefersFullscreenEdenChat());
    setIsTextPanelOpen(true);
  }, [isTextPanelOpen]);

  // The compact conversation preview opens the SAME conversation full screen.
  const openEdenFullscreen = useCallback(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setIsStatusPillDismissed(false);
    setIsEdenFullscreen(true);
    setIsTextPanelOpen(true);
  }, []);

  const toggleEdenFullscreen = useCallback(() => {
    setIsEdenFullscreen((full) => !full);
  }, []);

  // Governed change card → conversation. The outcome sentence comes from the
  // card's read-back of the campaign record, never from the request alone.
  const handleGovernedChangeOutcome = useCallback(
    (sentence) => {
      setLastAssistantReply(sentence);
    },
    [setLastAssistantReply]
  );
  const handleGovernedChangeFollowUp = useCallback(
    (text) => {
      submitVoiceTranscript(text, "typed");
    },
    [submitVoiceTranscript]
  );
  const handleGovernedChangeNavigate = useCallback(
    (href) => {
      if (typeof href === "string" && href.startsWith("/") && !href.startsWith("//")) navigate(href);
    },
    [navigate]
  );

  // Keep the newest turn in view as the conversation grows.
  useEffect(() => {
    if (!isTextPanelOpen) return;
    const scroller = isFullscreenSheetOpen ? textPanelBodyRef.current : textPanelRef.current;
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, [isTextPanelOpen, isFullscreenSheetOpen, conversationTurns.length, lastGovernedChange]);

  const stopManualVoiceSession = useCallback(() => {
    voiceInputControllerRef.current?.stop("user_stop");
  }, []);

  const handleTextPanelToggle = useCallback(() => {
    if (voiceInputSnapshot.active) {
      stopManualVoiceSession();
      return;
    }

    toggleTextPanel();
  }, [voiceInputSnapshot.active, stopManualVoiceSession, toggleTextPanel]);

  const handleMeetClick = useCallback(() => {
    window.open(EDEN_MEET_URL, "_blank", "noopener,noreferrer");
  }, []);

  // ---- Delegated-work status + persistent in-app notifications ----------
  const refreshDelegatedWork = useCallback(async () => {
    if (!delegatedWork?.id) return;
    const result = await fetchDelegatedWork(delegatedWork.id);
    if (result.ok && result.data?.work) {
      setDelegatedWork(result.data.work);
    }
  }, [delegatedWork?.id, setDelegatedWork]);

  // While the panel is open with active delegated work, poll for durable
  // progress. The backend row is authoritative — closing the panel (or the
  // whole browser) never affects the job.
  useEffect(() => {
    if (!isTextPanelOpen || !delegatedWork?.id) return undefined;
    const isActive = ["RECEIVED", "NEEDS_CLARIFICATION", "QUEUED", "IN_PROGRESS"].includes(
      delegatedWork.status
    );
    if (!isActive) return undefined;
    const timer = window.setInterval(refreshDelegatedWork, 15000);
    return () => window.clearInterval(timer);
  }, [isTextPanelOpen, delegatedWork?.id, delegatedWork?.status, refreshDelegatedWork]);

  // Reopening the conversation restores the latest work-status card without
  // forcing the panel open (fetch happens only once a panel is opened).
  useEffect(() => {
    if (!isTextPanelOpen || delegatedWork) return;
    let cancelled = false;
    (async () => {
      const active = await fetchActiveDelegatedWork();
      const activeWork = active.ok ? (active.data?.work || [])[0] : null;
      if (!cancelled && activeWork) {
        setDelegatedWork(activeWork);
        return;
      }
      const unread = await fetchUnreadWorkNotifications();
      const unreadWork = unread.ok ? (unread.data?.notifications || [])[0] : null;
      if (!cancelled && unreadWork) setDelegatedWork(unreadWork);
    })();
    return () => {
      cancelled = true;
    };
  }, [isTextPanelOpen, delegatedWork, setDelegatedWork]);

  // Unread READY_FOR_REVIEW / FAILED notifications drive the launcher badge.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      const result = await fetchUnreadWorkNotifications();
      if (!cancelled && result.ok) {
        setEdenWorkUnread(Array.isArray(result.data?.notifications) ? result.data.notifications : []);
      }
    };
    poll();
    const timer = window.setInterval(poll, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const handleOpenDraftFromWork = useCallback(
    (work) => {
      if (!work?.reviewPath) return;
      markWorkNotificationRead(work.id);
      setEdenWorkUnread((current) => current.filter((item) => item.id !== work.id));
      closeTextPanel();
      navigate(work.reviewPath);
    },
    [closeTextPanel, navigate]
  );

  const handleCancelDelegatedWork = useCallback(
    async (work) => {
      if (!work?.id) return;
      const result = await cancelDelegatedWork(work.id);
      if (result.ok && result.data?.work) setDelegatedWork(result.data.work);
    },
    [setDelegatedWork]
  );

  const handleClarifyDelegatedWork = useCallback(
    async (work, answerText) => {
      if (!work?.id || !answerText) return;
      const result = await clarifyDelegatedWork(work.id, { text: answerText });
      if (result.ok && result.data?.work) setDelegatedWork(result.data.work);
    },
    [setDelegatedWork]
  );

  const handleRetryDelegatedWork = useCallback(
    async (work) => {
      if (!work?.id) return;
      const result = await retryDelegatedWork(work.id);
      if (result.ok && result.data?.work) setDelegatedWork(result.data.work);
    },
    [setDelegatedWork]
  );
  const advertiserId = mergedEdenContext?.advertiser?.id ?? null;
  const timeframe = useMemo(
    () => ({
      start: effectivePageAwareness?.timeframe?.start || mergedEdenContext?.dateRange?.start || null,
      end: effectivePageAwareness?.timeframe?.end || mergedEdenContext?.dateRange?.end || null,
      datePreset:
        effectivePageAwareness?.timeframe?.datePreset ||
        ((effectivePageAwareness?.timeframe?.start || mergedEdenContext?.dateRange?.start) &&
        (effectivePageAwareness?.timeframe?.end || mergedEdenContext?.dateRange?.end)
          ? ""
          : "last_30d"),
    }),
    [
      effectivePageAwareness?.timeframe?.datePreset,
      effectivePageAwareness?.timeframe?.end,
      effectivePageAwareness?.timeframe?.start,
      mergedEdenContext?.dateRange?.end,
      mergedEdenContext?.dateRange?.start,
    ]
  );

  useEffect(() => {
    const hasRegisteredPageMetrics = effectivePageAwareness.pageToplineMetrics.length > 0;
    const hasRegisteredAggregateMetrics = effectivePageAwareness.aggregateToplineMetrics.length > 0;
    const shouldUseSharedToplineAuthority =
      effectivePageAwareness.toplineAuthority === "shared_fetch";
    const isRollupPage =
      effectivePageAwareness.platformKey === "social_rollup" ||
      effectivePageAwareness.platformKey === "programmatic_rollup";
    const shouldPreferAdvertiserWideCrossChannel =
      effectivePageAwareness.pageType === "analytics" &&
      effectivePageAwareness.toplineScope === "aggregate";
    const shouldFetchPlatformScoped =
      effectivePageAwareness.pageType === "analytics" &&
      Boolean(effectivePageAwareness.platformKey) &&
      !isRollupPage;
    if (
      !isTextPanelOpen ||
      !advertiserId ||
      (!shouldUseSharedToplineAuthority &&
        !shouldPreferAdvertiserWideCrossChannel &&
        (hasRegisteredPageMetrics || hasRegisteredAggregateMetrics))
    ) {
      setIsToplineLoading(false);
      setToplineData(null);
      return undefined;
    }

    let cancelled = false;
    setToplineData(null);

    const fetchTopline = async () => {
      try {
        if (!cancelled) setIsToplineLoading(true);
        const data = await getToplinePerformance({
          advertiserId,
          timeframe,
          platformKey: shouldFetchPlatformScoped ? effectivePageAwareness.platformKey : "",
        });
        if (!cancelled) {
          setToplineData(data);
        }
      } catch (error) {
        console.error("[EdenVoice][Client] topline fetch failed", error);
        if (!cancelled) {
          setToplineData(null);
        }
      } finally {
        if (!cancelled) {
          setIsToplineLoading(false);
        }
      }
    };

    fetchTopline();
    const intervalId = window.setInterval(fetchTopline, 45000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    advertiserId,
    effectivePageAwareness.aggregateToplineMetrics.length,
    effectivePageAwareness.pageToplineMetrics.length,
    effectivePageAwareness.pageType,
    effectivePageAwareness.platformKey,
    effectivePageAwareness.toplineScope,
    effectivePageAwareness.toplineAuthority,
    isTextPanelOpen,
    timeframe,
  ]);

  const toplinePeek = useMemo(() => {
    const advertiserName = String(mergedEdenContext?.advertiser?.name || "").trim();
    const pageTitle = String(effectivePageAwareness?.pageTitle || "").trim();
    const platformLabel = String(effectivePageAwareness?.platformLabel || "").trim();
    const dateStart = timeframe?.start || null;
    const dateEnd = timeframe?.end || null;
    const hasPageMetrics = effectivePageAwareness.pageToplineMetrics.length > 0;
    const hasAggregateMetrics = effectivePageAwareness.aggregateToplineMetrics.length > 0;
    const shouldUseSharedToplineAuthority =
      effectivePageAwareness.toplineAuthority === "shared_fetch";
    const shouldPreferAdvertiserWideCrossChannel =
      effectivePageAwareness.pageType === "analytics" &&
      effectivePageAwareness.toplineScope === "aggregate";
    const shouldPreferFetchedMetrics =
      shouldUseSharedToplineAuthority || shouldPreferAdvertiserWideCrossChannel;
    const isPlatformScopedFetch =
      ((!hasPageMetrics && !hasAggregateMetrics) || shouldUseSharedToplineAuthority) &&
      effectivePageAwareness.pageType === "analytics" &&
      Boolean(effectivePageAwareness.platformKey) &&
      effectivePageAwareness.platformKey !== "social_rollup" &&
      effectivePageAwareness.platformKey !== "programmatic_rollup";

    const scopedWindow =
      dateStart && dateEnd
        ? `${formatDateLabel(dateStart)} - ${formatDateLabel(dateEnd)}`
        : formatTimeframeLabel(effectivePageAwareness.timeframe);

    const scopeLabel =
      pageTitle ||
      toplineData?.scopeLabel ||
      advertiserName ||
      "Advertiser scope pending";
    const windowLabel = scopedWindow || toplineData?.windowLabel || "Live window pending";
    const topPlatform = String(toplineData?.topPlatform || "").trim();
    const topPlatformDisplay = topPlatform || EMPTY_TOP_PLATFORM_LABEL;
    const topPlatformHero = topPlatform ? resolveTopPlatformHero(topPlatform) : null;
    const pacingStatus = toplineData?.pacingStatus || "Awaiting data sync";
    const kpiLabel = toplineData?.costLabel || (/conversion|ticket|sales|authorize/i.test(String(mergedEdenContext?.page || ""))
      ? "CPA"
      : "CPC");
    const introLine = buildPageAssistantIntro(effectivePageAwareness);
    const registeredToplineInsight = String(effectivePageAwareness?.metadata?.toplineInsight || "").trim();
    const registeredToplineAnalysis = String(effectivePageAwareness?.metadata?.toplineAnalysis || "").trim();
    const analysisLine = buildGroundedEdenToplineAnalysis({
      registeredToplineAnalysis,
      toplineData,
      pageAwareness: effectivePageAwareness,
      scopeLabel,
    });
    const insightLine = sanitizeEdenClientFacingText(
      (shouldPreferFetchedMetrics
        ? toplineData?.insightSummary || registeredToplineInsight || introLine
        : hasPageMetrics
        ? registeredToplineInsight || introLine
        : hasAggregateMetrics
        ? registeredToplineInsight || toplineData?.insightSummary
        : toplineData?.insightSummary || registeredToplineInsight) ||
      (isToplineLoading
        ? "Syncing topline performance now..."
        : truncateLine(lastAssistantReply, 120) ||
          introLine)
    );

    const pageMetrics = shouldPreferFetchedMetrics
      ? buildMetricsFromFetchedTopline(toplineData, {
          platformLabel,
          isPlatformScoped: isPlatformScopedFetch,
        })
      : hasPageMetrics
      ? effectivePageAwareness.pageToplineMetrics.map((metric) => ({
          label: metric.label,
          value: metric.value,
          meta: normalizeEdenToplineCopy(metric.meta || "Current page context"),
          hero:
            metric.label.toLowerCase() === "platform" && topPlatformHero?.src
              ? topPlatformHero
              : null,
        }))
      : hasAggregateMetrics
      ? effectivePageAwareness.aggregateToplineMetrics.map((metric) => ({
          label: metric.label,
          value: metric.value,
          meta: normalizeEdenToplineCopy(metric.meta || "Current page context"),
          hero:
            metric.label.toLowerCase() === "platform" && topPlatformHero?.src
              ? topPlatformHero
              : null,
        }))
      : buildMetricsFromFetchedTopline(toplineData, {
          platformLabel,
          isPlatformScoped: isPlatformScopedFetch,
        });

    const winningSource = shouldPreferFetchedMetrics
      ? toplineData
        ? "shared_fetch_override"
        : "shared_fetch_pending"
      : hasPageMetrics
      ? "page"
      : hasAggregateMetrics
      ? "aggregate"
      : isPlatformScopedFetch
      ? "page_fetch"
      : "aggregate_fetch";

    if (process.env.NODE_ENV === "development") {
      console.debug("[EdenVoice][Client][ToplineMetrics]", {
        routeKey: effectivePageAwareness.routeKey,
        platformKey: effectivePageAwareness.platformKey,
        toplineScope: effectivePageAwareness.toplineScope,
        pageToplineMetricsDetected: hasPageMetrics,
        winningSource,
        metricKeys: Array.isArray(pageMetrics) ? pageMetrics.map((metric) => metric.label) : [],
      });
    }

    return {
      headerLabel: buildEdenHeaderLabel(pageTitle, platformLabel),
      kicker: pageTitle || "Topline Performance",
      scopeLabel,
      windowLabel,
      introLine,
      insightLine,
      analysisLine,
      metrics: (Array.isArray(pageMetrics) && pageMetrics.length
        ? pageMetrics
        : [
            {
              label: "Spend",
              value: toplineData?.spend || "—",
              meta: isToplineLoading ? "Syncing..." : toplineData?.raw?.snapshotSource || "Awaiting data sync",
            },
            {
              label: "Impressions",
              value: toplineData?.impressions || "—",
              meta: isToplineLoading ? "Syncing..." : "Latest delivery window",
            },
            {
              label: CANONICAL_CTR_LABEL,
              value: toplineData?.ctr || "—",
              meta: isToplineLoading ? "Syncing..." : "Cross-platform blended CTR",
            },
            {
              label: kpiLabel,
              value: toplineData?.cpc_or_cpa || "—",
              meta: isToplineLoading ? "Syncing..." : "Efficiency signal",
            },
            {
              label: "Top Platform",
              value: topPlatformDisplay,
              meta: topPlatform ? "Current Eden scope" : "Awaiting advertiser-scoped delivery",
              hero: topPlatformHero?.src ? topPlatformHero : null,
            },
            {
              label: "Pacing Status",
              value: pacingStatus,
              meta: toplineData?.pacingMeta || windowLabel,
            },
          ]).slice(0, 6),
    };
  }, [effectivePageAwareness, isToplineLoading, lastAssistantReply, mergedEdenContext, timeframe, toplineData]);

  const advertiserLogoMeta = useMemo(
    () =>
      normalizeAdvertiserLogoMeta({
        advertiserId: mergedEdenContext?.advertiser?.id ?? effectivePageAwareness?.advertiserId ?? null,
        logoUrl:
          effectivePageAwareness?.metadata?.logoUrl ??
          effectivePageAwareness?.metadata?.logo_url ??
          mergedEdenContext?.advertiser?.logoUrl ??
          mergedEdenContext?.advertiser?.logo_url ??
          "",
        logoDriveFileId:
          effectivePageAwareness?.metadata?.logoDriveFileId ??
          effectivePageAwareness?.metadata?.logo_drive_file_id ??
          mergedEdenContext?.advertiser?.logoDriveFileId ??
          mergedEdenContext?.advertiser?.logo_drive_file_id ??
          "",
        logoUpdatedAt:
          effectivePageAwareness?.metadata?.logoUpdatedAt ??
          effectivePageAwareness?.metadata?.logo_updated_at ??
          mergedEdenContext?.advertiser?.logoUpdatedAt ??
          mergedEdenContext?.advertiser?.logo_updated_at ??
          "",
        logoAltText:
          effectivePageAwareness?.metadata?.logoAltText ??
          effectivePageAwareness?.metadata?.logo_alt_text ??
          "",
      }),
    [effectivePageAwareness, mergedEdenContext]
  );

  const advertiserLogoCandidates = useMemo(
    () =>
      buildAdvertiserLogoCandidates({
        advertiserId: advertiserLogoMeta.advertiserId,
        logoUrl: advertiserLogoMeta.logoUrl,
        logoDriveFileId: advertiserLogoMeta.logoDriveFileId,
        logoUpdatedAt: advertiserLogoMeta.logoUpdatedAt,
      }),
    [
      advertiserLogoMeta.advertiserId,
      advertiserLogoMeta.logoDriveFileId,
      advertiserLogoMeta.logoUpdatedAt,
      advertiserLogoMeta.logoUrl,
    ]
  );
  const { resolvedSrc: advertiserLogoSrc, handleError: handleAdvertiserLogoError } =
    useResolvedImageCandidates(advertiserLogoCandidates, { apiBase: API_BASE_URL });

  const advertiserLogoAlt = useMemo(() => {
    const advertiserName = String(mergedEdenContext?.advertiser?.name || "").trim();
    return advertiserLogoMeta.logoAltText || `${advertiserName || "Advertiser"} logo`;
  }, [advertiserLogoMeta.logoAltText, mergedEdenContext]);

  const advertiserFallbackLabel = useMemo(
    () => buildAdvertiserFallbackLabel(mergedEdenContext?.advertiser?.name || ""),
    [mergedEdenContext]
  );

  const flightContext = useMemo(
    () => buildFlightContext({ timeframe: effectivePageAwareness?.timeframe || timeframe, toplineData }),
    [effectivePageAwareness?.timeframe, timeframe, toplineData]
  );

  useEffect(() => {
    setAdvertiserLogoBroken(false);
  }, [advertiserLogoSrc]);

  // Eden Entry overlay state + window listener so it can be opened from any route
  const [edenEntryVisible, setEdenEntryVisible] = useState(() => !hasDismissedEdenEntryThisSession());

  const handleDismissEdenEntry = useCallback(() => {
    setEdenEntryDismissedForSession(true);
    setEdenEntryVisible(false);
  }, []);

  const handleOpenEdenEntry = useCallback(() => {
    setEdenEntryDismissedForSession(false);
    setEdenEntryVisible(true);
  }, []);

  const handleSubmitEdenEntryPrompt = useCallback((prompt) => {
    return dispatchEdenEntryPrompt(prompt);
  }, []);

  useEffect(() => {
    window.addEventListener(EDEN_ENTRY_OPEN_EVENT, handleOpenEdenEntry);
    return () => window.removeEventListener(EDEN_ENTRY_OPEN_EVENT, handleOpenEdenEntry);
  }, [handleOpenEdenEntry]);

  if (shouldHide) return null;

  const hasStatus =
    voiceInputSnapshot.active ||
    isListening ||
    isSending ||
    isThinking ||
    Boolean(lastAssistantReply) ||
    Boolean(voiceNotice);
  const showDetachedStatus = hasStatus && !isTextPanelOpen && !isStatusPillDismissed;
  // Voice-session stages only. Eden working on a request is shown once, by
  // the indicator in Eden's reply slot, never as a second line here.
  const statusLine =
    voicePhase === "speaking"
      ? "Responding now…"
      : isVoiceCommandPending && !isSending
      ? "PROCESSING VOICE COMMAND…"
      : null;
  const hearingTranscript =
    String(interimTranscript || voiceCommandTranscript || "").trim();
  const visibleUserUtterance = voiceCommandTranscript
    ? ""
    : lastUserUtterance;
  // The conversation the latest exchange belongs to: earlier turns above it,
  // and any Eden lines between the latest request and the newest reply (for
  // example a governed proposal followed by its verified outcome).
  let lastUserTurnIndex = -1;
  for (let i = conversationTurns.length - 1; i >= 0; i -= 1) {
    if (conversationTurns[i].role === "user") {
      lastUserTurnIndex = i;
      break;
    }
  }
  const earlierTurns = lastUserTurnIndex > 0 ? conversationTurns.slice(0, lastUserTurnIndex) : [];
  const interimEdenTurns =
    lastUserTurnIndex >= 0
      ? conversationTurns.slice(lastUserTurnIndex + 1, -1).filter((turn) => turn.role === "eden")
      : [];
  // The newest reply answers the newest request only once Eden has replied
  // to it. Until then the previous reply stays where it is in the history
  // above, instead of appearing a second time as if it answered the question.
  const latestTurn = conversationTurns[conversationTurns.length - 1] || null;
  const visibleAssistantReply = latestTurn?.role === "user" ? "" : lastAssistantReply;
  // "Voice input stopped" is about a voice session; a typed conversation
  // that never used the microphone has none to report.
  const showVoiceInputStatus = voiceInputWasUsed || voiceInputSnapshot.phase !== "idle";
  const voiceButtonLabel = voiceInputSnapshot.active
    ? "Stop Eden voice session"
    : isTextPanelOpen
    ? "Collapse Eden panel"
    : "Open Eden panel";
  const edenEntryButtonLabel = edenEntryVisible ? "Eden Entry is open" : "Open Eden Entry";
  const edenEntryButtonTitle = isSending || isThinking
    ? "Eden is analysing your request"
    : edenEntryButtonLabel;

  const stopVoiceCaptureIfNeeded = () => {
    if (voiceInputSnapshot.active) {
      logVoiceState("typed_prompt_stops_listening");
    }
    voiceInputControllerRef.current?.stop("typed_prompt");
    clearInterimTranscript();
  };

  const submitTypedPrompt = async () => {
    const cleanPrompt = String(typedPrompt || "").trim();
    if (!cleanPrompt) return;

    stopVoiceCaptureIfNeeded();
    typedDraftBeforeVoiceRef.current = "";
    setVoiceCommandTranscript("");
    setTypedPrompt("");
    setVoiceNotice("");
    setIsStatusPillDismissed(false);
    setIsTextPanelOpen(true);
    logVoiceState("typed_prompt_dispatched", { transcript: cleanPrompt });
    await submitVoiceTranscript(cleanPrompt, "typed");
  };

  const handleTypedPromptKeyDown = async (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      await submitTypedPrompt();
    }
  };

  const handleMicClick = () => {
    dispatchEdenEntryOpen();
    setIsTextPanelOpen(false);
    setIsEdenFullscreen(false);
  };

  const handleStartVoiceClick = () => {
    if (voiceInputSnapshot.active) {
      stopManualVoiceSession();
      return;
    }
    setIsStatusPillDismissed(false);
    minimizeAfterVoiceCommandRef.current = false;
    typedDraftBeforeVoiceRef.current = typedPrompt;
    clearInterimTranscript();
    setVoiceCommandTranscript("");
    setVoiceNotice("");
    if (!isTextPanelOpen && prefersFullscreenEdenChat()) setIsEdenFullscreen(true);
    setIsTextPanelOpen(true);
    voiceInputControllerRef.current?.start();
  };

  return (
    <>
      <div
        className={`cphere-voice-launcher-stack ${
          isTextPanelOpen ? "cphere-voice-launcher-stack--panel-open" : "cphere-voice-launcher-stack--panel-closed"
        }${edenEntryVisible ? " cphere-voice-launcher-stack--eden-entry-open" : ""}${
          isFullscreenSheetOpen ? " cphere-voice-launcher-stack--fullscreen" : ""
        }`}
      >
        <button
          className={`cphere-voice-panel-toggle ${isTextPanelOpen ? "open" : ""}`}
          onClick={handleTextPanelToggle}
          type="button"
          aria-label={
            edenWorkUnread.length > 0
              ? `${voiceButtonLabel} — ${edenWorkUnread.length} delegated ${
                  edenWorkUnread.length === 1 ? "draft is" : "drafts are"
                } ready to review`
              : voiceButtonLabel
          }
          aria-expanded={isTextPanelOpen}
          aria-controls="eden-text-panel-client"
        >
          <span className="cphere-voice-panel-toggle-label">Ask</span>
          {edenWorkUnread.length > 0 && (
            <span className="cphere-voice-panel-toggle-badge" role="status">
              {edenWorkUnread.length}
            </span>
          )}
        </button>

        {/* Fully unmounted when closed: no hidden composer, no focusable
            remnants, no click-intercepting container. Conversation history
            survives in the voice provider and is restored on reopen. */}
        {isTextPanelOpen && (
        <div
          id="eden-text-panel-client"
          ref={textPanelRef}
          className={`cphere-voice-text-panel open${isFullscreenSheetOpen ? " cphere-voice-text-panel--fullscreen" : ""}`}
          role={isFullscreenSheetOpen ? "dialog" : undefined}
          aria-modal={isFullscreenSheetOpen ? "true" : undefined}
          aria-label={isFullscreenSheetOpen ? "Eden conversation" : undefined}
        >
          <div className="cphere-voice-text-panel-header">
            <div className="cphere-voice-text-panel-title-stack">
              <div className="cphere-voice-text-panel-kicker">Eden • Analyst Copilot</div>
              <div className="cphere-voice-text-panel-page-label">{toplinePeek.headerLabel}</div>
            </div>
            <div className="cphere-voice-text-panel-header-actions">
              <button
                className={`cphere-voice-panel-expand-toggle ${isToplineOpen ? "open" : ""}`}
                type="button"
                onClick={() => setIsToplineOpen((prev) => !prev)}
                aria-expanded={isToplineOpen}
                aria-controls="eden-topline-peek-client"
              >
                <span className="cphere-voice-panel-expand-label">Topline</span>
                <span className="cphere-voice-panel-expand-icon" aria-hidden="true">
                  ▾
                </span>
              </button>
              <button
                className="cphere-voice-text-panel-fullscreen-toggle"
                type="button"
                onClick={toggleEdenFullscreen}
                aria-pressed={isEdenFullscreen}
                aria-label={isEdenFullscreen ? "Exit full screen" : "Open Eden full screen"}
                title={isEdenFullscreen ? "Exit full screen" : "Full screen"}
              >
                <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
                  {isEdenFullscreen ? (
                    <path d="M6 1v5H1M10 1v5h5M6 15v-5H1M10 15v-5h5" fill="none" stroke="currentColor" strokeWidth="1.6" />
                  ) : (
                    <path d="M1 6V1h5M15 6V1h-5M1 10v5h5M15 10v5h-5" fill="none" stroke="currentColor" strokeWidth="1.6" />
                  )}
                </svg>
              </button>
              {isFullscreenSheetOpen ? (
                <button
                  className="cphere-voice-text-panel-close cphere-voice-text-panel-close--back"
                  type="button"
                  onClick={minimizeTextPanel}
                  aria-label="Back to the page; the conversation stays in the preview"
                >
                  <span className="cphere-voice-text-panel-back-label">
                    <span aria-hidden="true">‹</span> Back
                  </span>
                </button>
              ) : (
              <button
                className="cphere-voice-text-panel-close"
                type="button"
                onClick={closeTextPanel}
                aria-label="Close Eden text panel"
              >
                <span className="cphere-voice-text-panel-close-icon" aria-hidden="true">
                  ×
                </span>
              </button>
              )}
            </div>
          </div>

          <div className="cphere-voice-text-panel-body" ref={textPanelBodyRef}>
            {earlierTurns.length > 0 && (
              <ol className="cphere-voice-conversation-history" aria-label="Earlier in this conversation">
                {earlierTurns.map((turn) => (
                  <li
                    key={turn.id}
                    className={`cphere-voice-panel-line cphere-voice-panel-line-history eden-speaker-line${
                      turn.role === "eden" ? " cphere-voice-panel-line-eden" : ""
                    }`}
                  >
                    <EdenSpeakerAvatar speaker={turn.role === "eden" ? "eden" : "you"} />
                    <strong>{turn.role === "eden" ? "Eden:" : "You:"}</strong> {turn.text}
                  </li>
                ))}
              </ol>
            )}

            {showVoiceInputStatus && <EdenVoiceInputStatus snapshot={voiceInputSnapshot} />}

            {statusLine && <div className="cphere-voice-panel-status">{statusLine}</div>}

            {voiceNotice && <div className="cphere-voice-panel-status">{voiceNotice}</div>}

            {hearingTranscript && (
              <div className="cphere-voice-panel-line cphere-voice-panel-line-interim" title={hearingTranscript}>
                <strong>Hearing:</strong> {hearingTranscript}
              </div>
            )}

            {visibleUserUtterance && (
              <div className="cphere-voice-panel-line eden-speaker-line" title={visibleUserUtterance}>
                <EdenSpeakerAvatar speaker="you" />
                <strong>You:</strong> {visibleUserUtterance}
              </div>
            )}

            {interimEdenTurns.map((turn) => (
              <div key={turn.id} className="cphere-voice-panel-line cphere-voice-panel-line-eden eden-speaker-line">
                <EdenSpeakerAvatar speaker="eden" />
                <strong>Eden:</strong> {turn.text}
              </div>
            ))}

            {isAwaitingReply && <EdenThinkingLine className="cphere-voice-panel-line cphere-voice-panel-line-eden" />}

            {visibleAssistantReply && (
              <div className="cphere-voice-panel-line cphere-voice-panel-line-eden eden-speaker-line" title={visibleAssistantReply}>
                <EdenSpeakerAvatar speaker="eden" />
                <strong>Eden:</strong> {visibleAssistantReply}
              </div>
            )}

            <EdenActivityAnnouncer
              announcement={isAwaitingReply ? "Eden is thinking." : visibleAssistantReply ? "Eden replied." : ""}
            />

            <EdenChatVisualizationRenderer visualizations={lastAssistantVisualizations} />

            <EdenWorkStatusCard
              work={delegatedWork}
              onOpenDraft={handleOpenDraftFromWork}
              onCancel={handleCancelDelegatedWork}
              onClarify={handleClarifyDelegatedWork}
              onRetry={handleRetryDelegatedWork}
            />

            {lastGovernedChange && (
              <EdenGovernedChangeCard
                key={lastGovernedChange.id}
                change={lastGovernedChange}
                onOutcome={handleGovernedChangeOutcome}
                onFollowUp={handleGovernedChangeFollowUp}
                onNavigate={handleGovernedChangeNavigate}
              />
            )}

          </div>

          <div
            id="eden-topline-peek-client"
            className={`cphere-voice-topline-peek ${isToplineOpen ? "open" : ""}`}
            aria-hidden={!isToplineOpen}
          >
            <div className="cphere-voice-topline-peek-shell">
              <div className="cphere-voice-topline-peek-header">
                <div className="cphere-voice-topline-peek-brand">
                  <div className="cphere-voice-topline-advertiser-logo-slot">
                    {advertiserLogoSrc && !advertiserLogoBroken ? (
                      <div className="cphere-voice-topline-advertiser-logo-shell">
                        <img
                          className="cphere-voice-topline-advertiser-logo"
                          src={advertiserLogoSrc}
                          alt={advertiserLogoAlt}
                          onError={() => {
                            setAdvertiserLogoBroken(true);
                            handleAdvertiserLogoError();
                          }}
                        />
                      </div>
                    ) : (
                      <div className="cphere-voice-topline-advertiser-logo-fallback">
                        <span className="cphere-voice-topline-advertiser-logo-fallback-text">
                          {advertiserFallbackLabel}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="cphere-voice-topline-peek-flight">
                  <div className="cphere-voice-topline-peek-flight-label">
                    {flightContext.label}
                  </div>
                  <div className="cphere-voice-topline-peek-window">{flightContext.value}</div>
                </div>
              </div>

              <div className="cphere-voice-topline-grid">
                {toplinePeek.metrics.map((metric) => (
                  <div key={metric.label} className="cphere-voice-topline-card">
                    <div className="cphere-voice-topline-label">{metric.label}</div>
                    {metric.hero?.src ? (
                      <div className="cphere-voice-topline-platform-hero" aria-hidden="true">
                        <span className="cphere-voice-topline-platform-hero-ring">
                          <img
                            className="cphere-voice-topline-platform-hero-image"
                            src={metric.hero.src}
                            alt={metric.hero.alt || metric.value}
                          />
                        </span>
                      </div>
                    ) : null}
                    <div
                      className={`cphere-voice-topline-value ${/\d/.test(String(metric.value || "")) ? "is-numeric" : ""}`}
                    >
                      {metric.value}
                    </div>
                    <div className="cphere-voice-topline-meta">{metric.meta}</div>
                  </div>
                ))}
              </div>

              <div className="cphere-voice-topline-insight">{toplinePeek.insightLine}</div>
              {toplinePeek.analysisLine ? (
                <div className="cphere-voice-topline-analysis">{toplinePeek.analysisLine}</div>
              ) : null}
            </div>
          </div>

          <div className="cphere-voice-text-panel-compose">
            <textarea
              ref={textInputRef}
              className="cphere-voice-textarea"
              placeholder="Ask Eden…"
              value={typedPrompt}
              onChange={(event) => setTypedPrompt(event.target.value)}
              onKeyDown={handleTypedPromptKeyDown}
              rows={3}
            />

            <div className="cphere-voice-text-panel-actions">
              <EdenVoiceInputButton
                snapshot={voiceInputSnapshot}
                onClick={handleStartVoiceClick}
              />
              <button
                className="cphere-voice-panel-action cphere-voice-panel-action-meet"
                type="button"
                onClick={handleMeetClick}
              >
                Meet
              </button>
              <button
                className="cphere-voice-panel-action cphere-voice-panel-action-magenta"
                type="button"
                onClick={submitTypedPrompt}
                disabled={!typedPrompt.trim()}
              >
                Send
              </button>
            </div>
          </div>
        </div>
        )}

        <button
          className={`cphere-voice-mic-button ${isListening ? "listening" : ""} ${
            isTextPanelOpen ? "open" : ""
          } ${
            isSending || isThinking ? "processing" : ""
          }`}
          onClick={handleMicClick}
          aria-label={edenEntryButtonLabel}
          aria-expanded={edenEntryVisible}
          aria-controls="eden-entry-prompt"
          aria-haspopup="dialog"
          title={edenEntryButtonTitle}
          type="button"
        >
          <span className="cphere-voice-button-label" aria-hidden="true">
            Eden
          </span>
        </button>
      </div>

      {showDetachedStatus && (
        <div className="cphere-voice-status-pill cphere-voice-status-pill--opens-chat">
          {/* The whole preview opens the same conversation full screen. */}
          <button
            type="button"
            className="cphere-voice-status-pill-open"
            onClick={openEdenFullscreen}
            aria-label="Open the Eden conversation full screen"
            aria-controls="eden-text-panel-client"
            aria-haspopup="dialog"
          />
          <div
            className="cphere-voice-status-pill-live"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
          {isListening && <span className="dot dot-live" />}
          {(isSending || isThinking) && <span className="dot dot-processing" />}
          {isSpeaking && <span className="dot dot-speaking" />}

          <div className="cphere-voice-text">
            <div className="line small line-kicker">Eden • Voice Analyst</div>
            {statusLine && <div className="line small">{statusLine}</div>}

            {voiceNotice && <div className="line small">{voiceNotice}</div>}

            {hearingTranscript && (
              <div className="line interim" title={hearingTranscript}>
                <strong>Hearing:</strong> {hearingTranscript}
              </div>
            )}

            {visibleUserUtterance && (
              <div className="line user eden-speaker-line" title={visibleUserUtterance}>
                <EdenSpeakerAvatar speaker="you" />
                <strong>You:</strong> {visibleUserUtterance}
              </div>
            )}

            {visibleAssistantReply && (
              <div className="line sherloc eden-speaker-line" title={visibleAssistantReply}>
                <EdenSpeakerAvatar speaker="eden" />
                <strong>Eden:</strong> {visibleAssistantReply}
              </div>
            )}
          </div>
          </div>
        </div>
      )}
      {/* Global Eden Entry overlay mounted here so the Eden orb opens everywhere */}
      <EdenEntryOverlay
        visible={edenEntryVisible}
        displayName={
          (function resolveDisplayName() {
            try {
              const storedPreferred = window.localStorage.getItem("preferredDisplayName");
              const storedDisplay = window.localStorage.getItem("displayName");
              const candidates = [
                mergedEdenContext?.viewer || {},
                { preferredDisplayName: storedPreferred, displayName: storedDisplay },
              ];
              const fields = ["firstName", "preferredDisplayName", "displayName", "name", "fullName"];
              for (const src of candidates) {
                for (const f of fields) {
                  const v = src?.[f];
                  if (v && String(v).trim()) return String(v).trim();
                }
              }
            } catch {}
            return "";
          })()
        }
        onDismiss={handleDismissEdenEntry}
        onSubmit={handleSubmitEdenEntryPrompt}
      />
    </>
  );
};

// The controller's own state (the unsent draft, notices, the open panel, the
// topline peek) is account-owned too: each Eden session gets a fresh
// controller, so none of it reaches the next signed-in account.
const GlobalVoiceController = () => {
  const { edenSessionGeneration } = useSherlocVoice();
  return <GlobalVoiceControllerSession key={edenSessionGeneration ?? 0} />;
};

export default GlobalVoiceController;
